import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field } from "@/components/ui-kit";
import { toast } from "sonner";
import { Save, ShieldAlert, Gauge, ShieldCheck, Archive, MessageSquareHeart, KeyRound, FileSignature, ClipboardList } from "lucide-react";

const MFA_ROLE_OPTIONS = [
  { value: "agency_admin", label: "Agency admins" },
  { value: "dispatcher", label: "Dispatchers" },
  { value: "client_user", label: "Client portal users" },
  { value: "driver", label: "Drivers" },
];

const ROLE_LABELS = { agency_admin: "Agency Admin", dispatcher: "Dispatcher", client_user: "Client Portal", driver: "Driver" };

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    off_route_threshold_m: 300,
    speed_tolerance_mph: 3,
    compliance_gate_enabled: true,
    retention_years: 6,
    purge_enabled: false,
    rider_feedback_emails: false,
    security_alerts_enabled: true,
    security_alert_email: "",
    mfa_required_roles: [],
    mndot_number: "",
    provider_address: "",
  });
  const [saving, setSaving] = useState(false);

  const [teamUsers, setTeamUsers] = useState([]);

  useEffect(() => {
    api.get("/agency/settings").then((r) => setSettings((s) => ({
      ...s,
      ...r.data,
      retention_years: r.data.retention_years ?? 6,
      purge_enabled: !!r.data.purge_enabled,
      rider_feedback_emails: !!r.data.rider_feedback_emails,
      security_alerts_enabled: r.data.security_alerts_enabled !== false,
      security_alert_email: r.data.security_alert_email || "",
      mfa_required_roles: r.data.mfa_required_roles || [],
      mndot_number: r.data.mndot_number || "",
      provider_address: r.data.provider_address || "",
    })));
  }, []);

  const loadTeam = () => {
    api.get("/team/users")
      .then((r) => setTeamUsers(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  useEffect(() => {
    loadTeam();
  }, []);

  const toggleMfaRole = (role) => {
    setSettings((s) => {
      const current = s.mfa_required_roles || [];
      const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
      return { ...s, mfa_required_roles: next };
    });
  };

  const resetMfa = async (teamUser) => {
    if (!window.confirm(`Reset MFA for ${teamUser.email}? They will be able to sign in with a password alone until they set it up again.`)) return;
    try {
      await api.post(`/team/users/${teamUser.id}/mfa/reset`);
      toast.success("MFA reset");
      loadTeam();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/agency/settings", {
        off_route_threshold_m: Number(settings.off_route_threshold_m),
        speed_tolerance_mph: Number(settings.speed_tolerance_mph),
        compliance_gate_enabled: !!settings.compliance_gate_enabled,
        retention_years: Number(settings.retention_years) || 6,
        purge_enabled: !!settings.purge_enabled,
        rider_feedback_emails: !!settings.rider_feedback_emails,
        security_alerts_enabled: !!settings.security_alerts_enabled,
        security_alert_email: settings.security_alert_email || null,
        mfa_required_roles: settings.mfa_required_roles || [],
        mndot_number: settings.mndot_number || null,
        provider_address: settings.provider_address || null,
      });
      setSettings((s) => ({ ...s, ...data, mfa_required_roles: data.mfa_required_roles || [] }));
      toast.success("Rules saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader subtitle="Configuration" title="Alert Rules" />
      <button
        onClick={() => navigate("/app/settings/agreements")}
        className="max-w-2xl w-full mb-6 flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 text-left"
        data-testid="settings-agreements-link"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-sky-50 text-sky-700 border border-sky-200 flex items-center justify-center flex-shrink-0"><FileSignature size={18} /></div>
          <div>
            <div className="font-display font-semibold text-slate-900">Agreements</div>
            <div className="text-xs text-slate-500">Fraud, Waste and Abuse training, the Independent Contractor agreement, and any custom agreements your drivers sign.</div>
          </div>
        </div>
        <span className="text-xs text-[#005E82] flex-shrink-0">Manage</span>
      </button>
      <div className="max-w-2xl bg-white border border-slate-200 rounded-md p-6">
        <div className="text-sm text-slate-600 mb-6">
          Tune what counts as "unsafe" for your fleet. Changes apply immediately to every driver ping — they don't affect alerts already generated.
        </div>

        <div className="space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-700 border border-amber-200 flex items-center justify-center flex-shrink-0"><ShieldAlert size={18} /></div>
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Off-route threshold</div>
              <div className="text-xs text-slate-500 mb-2">Distance from planned OSRM route before we open a drift alert. Default 300 m.</div>
              <div className="flex items-center gap-2">
                <Field label="Meters" testId="off-route-threshold" type="number" value={settings.off_route_threshold_m} onChange={(e) => setSettings({ ...settings, off_route_threshold_m: e.target.value })} />
                <div className="text-xs text-slate-400 mt-6 whitespace-nowrap">min 50 · max 5000</div>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-rose-50 text-rose-700 border border-rose-200 flex items-center justify-center flex-shrink-0"><Gauge size={18} /></div>
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Speed tolerance</div>
              <div className="text-xs text-slate-500 mb-2">Additional mph over the vehicle limit before we open a speeding alert. Default 3 mph.</div>
              <div className="flex items-center gap-2">
                <Field label="mph over limit" testId="speed-tolerance" type="number" value={settings.speed_tolerance_mph} onChange={(e) => setSettings({ ...settings, speed_tolerance_mph: e.target.value })} />
                <div className="text-xs text-slate-400 mt-6 whitespace-nowrap">min 0 · max 30</div>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center flex-shrink-0"><ShieldCheck size={18} /></div>
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Compliance gate</div>
              <div className="text-xs text-slate-500 mb-3">Block ride assignment to drivers with any expired or missing compliance documents. Agency admin can still override per-ride with a checkbox.</div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!settings.compliance_gate_enabled}
                  onChange={(e) => setSettings({ ...settings, compliance_gate_enabled: e.target.checked })}
                  data-testid="gate-toggle"
                />
                Enforce gate — reject ride assignment if driver has any expired or missing docs
              </label>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-teal-50 text-teal-700 border border-teal-200 flex items-center justify-center flex-shrink-0"><ClipboardList size={18} /></div>
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">MnDOT number</div>
              <div className="text-xs text-slate-500 mb-2">
                Printed at the top of the STS Driver List and STS Vehicle List (Compliance page). Leave blank until MnDOT assigns
                your provider number, the rosters still print without it.
              </div>
              <Field
                label="MnDOT provider number"
                testId="mndot-number"
                value={settings.mndot_number}
                onChange={(e) => setSettings({ ...settings, mndot_number: e.target.value })}
                placeholder="e.g. MN-STS-04471"
              />
              <div className="mt-3">
                <Field
                  label="Provider address (as registered with MnDOT)"
                  testId="provider-address"
                  value={settings.provider_address}
                  onChange={(e) => setSettings({ ...settings, provider_address: e.target.value })}
                  placeholder="Street, City, State ZIP"
                />
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-slate-50 text-slate-700 border border-slate-200 flex items-center justify-center flex-shrink-0"><Archive size={18} /></div>
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Data retention</div>
              <div className="text-xs text-slate-500 mb-3">
                Soft-deleted records are kept for this many years, then removed by the nightly purge if enabled. Minnesota DHS requires
                Medical Assistance records to be kept at least 5 years; HIPAA requires compliance documentation for 6.
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Field
                  label="Years"
                  testId="retention-years"
                  type="number"
                  min="1"
                  max="10"
                  value={settings.retention_years}
                  onChange={(e) => setSettings({ ...settings, retention_years: e.target.value })}
                />
                <div className="text-xs text-slate-400 mt-6 whitespace-nowrap">min 1 · max 10</div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!settings.purge_enabled}
                  onChange={(e) => setSettings({ ...settings, purge_enabled: e.target.checked })}
                  data-testid="purge-enabled-toggle"
                />
                Enable nightly purge, permanently remove soft-deleted records once past the retention window
              </label>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-pink-50 text-pink-700 border border-pink-200 flex items-center justify-center flex-shrink-0"><MessageSquareHeart size={18} /></div>
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Rider feedback emails</div>
              <div className="text-xs text-slate-500 mb-3">
                When a ride is completed, email the rider a short feedback link. The email contains the rider's first name and the link only,
                no trip details. Off by default because the email provider does not sign a HIPAA business associate agreement; turn it on only
                if that is acceptable for your agency.
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!settings.rider_feedback_emails}
                  onChange={(e) => setSettings({ ...settings, rider_feedback_emails: e.target.checked })}
                  data-testid="rider-feedback-emails-toggle"
                />
                Email riders a feedback link when their ride is completed
              </label>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-sky-50 text-sky-700 border border-sky-200 flex items-center justify-center flex-shrink-0"><ShieldAlert size={18} /></div>
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Security alerts</div>
              <div className="text-xs text-slate-500 mb-3">
                Lockouts, sign-ins from new addresses, bursts of failed sign-ins and unusual download or export volumes
                are recorded on the Security page. Medium and high alerts are also emailed to the address below. These
                messages describe staff accounts and IP addresses only, never riders.
              </div>
              <label className="flex items-center gap-2 text-sm mb-3">
                <input
                  type="checkbox"
                  checked={!!settings.security_alerts_enabled}
                  onChange={(e) => setSettings({ ...settings, security_alerts_enabled: e.target.checked })}
                  data-testid="security-alerts-enabled-toggle"
                />
                Record security alerts
              </label>
              <Field
                label="Security contact email"
                testId="security-alert-email"
                type="email"
                value={settings.security_alert_email}
                onChange={(e) => setSettings({ ...settings, security_alert_email: e.target.value })}
                placeholder="security@your-agency.com"
              />
              <div className="text-xs text-slate-400 mt-1">Leave blank to keep alerts in-app only.</div>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-violet-50 text-violet-700 border border-violet-200 flex items-center justify-center flex-shrink-0"><KeyRound size={18} /></div>
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Require two-step sign-in (MFA)</div>
              <div className="text-xs text-slate-500 mb-3">
                Users in the selected roles must set up an authenticator app before they can use anything but their account page. Enable
                MFA on your own account first, the server refuses to lock you out.
              </div>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {MFA_ROLE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(settings.mfa_required_roles || []).includes(opt.value)}
                      onChange={() => toggleMfaRole(opt.value)}
                      data-testid={`mfa-role-${opt.value}`}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>

              <div className="pt-4 border-t border-slate-100">
                <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-2">Team MFA status</div>
                <div className="overflow-x-auto border border-slate-200 rounded-md" data-testid="team-mfa-table">
                  <table className="w-full text-sm">
                    <thead className="text-[11px] uppercase tracking-[0.1em] text-slate-500 bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2">Name</th>
                        <th className="text-left px-3 py-2">Email</th>
                        <th className="text-left px-3 py-2">Role</th>
                        <th className="text-left px-3 py-2">MFA</th>
                        <th className="text-left px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamUsers.length === 0 && (
                        <tr>
                          <td colSpan={5}><div className="empty">No team members yet.</div></td>
                        </tr>
                      )}
                      {teamUsers.map((tu) => (
                        <tr key={tu.id} className="border-t border-slate-100" data-testid={`team-row-${tu.id}`}>
                          <td className="px-3 py-2 text-slate-900">{tu.name}</td>
                          <td className="px-3 py-2 text-slate-600">{tu.email}</td>
                          <td className="px-3 py-2 text-xs uppercase tracking-wide text-slate-500">{ROLE_LABELS[tu.role] || tu.role}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                                tu.mfa_enabled ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"
                              }`}
                            >
                              {tu.mfa_enabled ? "On" : "Off"}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {tu.mfa_enabled && tu.id !== user?.id && (
                              <button
                                onClick={() => resetMfa(tu)}
                                className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400"
                                data-testid={`mfa-reset-${tu.id}`}
                              >
                                Reset MFA
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-1.5" data-testid="save-settings-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save rules"}
          </button>
        </div>
      </div>
    </div>
  );
}
